import type { HsIndexedGeometry } from '../graphic-engine-new/geometry/indexed-geometry.class';
import { HsSpriteGeometry } from '../graphic-engine-new/geometry/sprite-geometry.class';
import { HsTexturedIndexedGeometry } from '../graphic-engine-new/geometry/textured-indexed-geometry.class';
import { HsGraphicConstants } from './constants/graphic-constants.namespace';
import type { HsGraphicCollection } from './objects/graphic-collection.class';
import type { HsShader } from './shaders/classes/shader.class';
import type { HsGraphicsBuffer } from './types/graphic-buffer.type';

const INSTANCE_MATRIX_ATTRIBUTES: readonly string[] = [
  'a_relativeMatrix0',
  'a_relativeMatrix1',
  'a_relativeMatrix2',
  'a_relativeMatrix3',
];

interface VertexArrayRecord {
  vertexArray: WebGLVertexArrayObject;
  layout: string;
  geometry: HsIndexedGeometry;
  collection: HsGraphicCollection | null;
  matrixBuffer: HsGraphicsBuffer | null;
  colorBuffer: HsGraphicsBuffer | null;
  usedAt: number;
}

/**
 * Vertex array objects for meshes and instanced collections drawn one by one, so a draw binds one object instead of
 * re-specifying index buffer, vertex pointers, enabled arrays and instance divisors.
 *
 * Every active attribute of the shader gets an array, so a vertex array is only made when the renderer knows all of
 * them; any other combination keeps using the default vertex array. A vertex array keeps deleted buffers alive, so
 * arrays of deleted or long unused geometry are released by sweep().
 */
export class HsVertexArrayCache {
  private readonly records: Map<HsIndexedGeometry | HsGraphicCollection, VertexArrayRecord[]> = new Map();
  private lastSweepAt: number = Number.NEGATIVE_INFINITY;
  private count: number = 0;

  public static readonly sweepIntervalMs: number = 1000;
  public static readonly unusedLifetimeMs: number = 10000;

  public get size(): number {
    return this.count;
  }

  /**
   * Binds the vertex array of the geometry, or of the collection's geometry and instance buffers, for the shader's
   * attribute layout. Returns false, with no vertex array bound, when the default vertex array has to be used.
   */
  public bind(
    gl: WebGL2RenderingContext,
    shader: HsShader,
    geometry: HsIndexedGeometry,
    collection: HsGraphicCollection | null,
    now: number
  ): boolean {
    if (geometry instanceof HsSpriteGeometry) {
      return false;
    }
    if (collection !== null && (collection.matrixBuffer === null || collection.colorBuffer === null)) {
      return false;
    }
    const owner: HsIndexedGeometry | HsGraphicCollection = collection ?? geometry;
    const layout: string = shader.attributeLayout;
    let records: VertexArrayRecord[] | undefined = this.records.get(owner);
    if (records === undefined) {
      records = [];
      this.records.set(owner, records);
    }
    for (let i: number = 0; i < records.length; i++) {
      const record: VertexArrayRecord = records[i];
      if (record.layout !== layout) {
        continue;
      }
      if (
        record.geometry === geometry &&
        record.matrixBuffer === (collection?.matrixBuffer ?? null) &&
        record.colorBuffer === (collection?.colorBuffer ?? null)
      ) {
        record.usedAt = now;
        gl.bindVertexArray(record.vertexArray);
        return true;
      }
      gl.deleteVertexArray(record.vertexArray);
      records.splice(i, 1);
      this.count--;
      break;
    }
    const record: VertexArrayRecord | null = this.create(gl, shader, layout, geometry, collection, now);
    if (record === null) {
      if (records.length === 0) {
        this.records.delete(owner);
      }
      return false;
    }
    records.push(record);
    this.count++;
    return true;
  }

  /** Releases vertex arrays of deleted geometry and of geometry not drawn recently, at most once per interval. */
  public sweep(gl: WebGL2RenderingContext, now: number, force: boolean = false): void {
    if (!force && now - this.lastSweepAt < HsVertexArrayCache.sweepIntervalMs) {
      return;
    }
    this.lastSweepAt = now;
    this.records.forEach((records: VertexArrayRecord[], owner: HsIndexedGeometry | HsGraphicCollection) => {
      for (let i: number = records.length - 1; i >= 0; i--) {
        const record: VertexArrayRecord = records[i];
        if (
          record.geometry.isDeleted ||
          record.collection?.isDeleted === true ||
          now - record.usedAt > HsVertexArrayCache.unusedLifetimeMs
        ) {
          gl.deleteVertexArray(record.vertexArray);
          records.splice(i, 1);
          this.count--;
        }
      }
      if (records.length === 0) {
        this.records.delete(owner);
      }
    });
  }

  public clear(gl: WebGL2RenderingContext): void {
    this.records.forEach((records: VertexArrayRecord[]) => {
      records.forEach((record: VertexArrayRecord) => gl.deleteVertexArray(record.vertexArray));
    });
    this.records.clear();
    this.count = 0;
  }

  private create(
    gl: WebGL2RenderingContext,
    shader: HsShader,
    layout: string,
    geometry: HsIndexedGeometry,
    collection: HsGraphicCollection | null,
    now: number
  ): VertexArrayRecord | null {
    const positions: number = shader.getAttribute('a_positions');
    const normals: number = shader.getAttribute('a_normals');
    const textureCoordinates: number = shader.getAttribute('a_texcoord');
    const color: number = shader.getAttribute('a_color');
    let known: number = Number(positions !== -1) + Number(normals !== -1) + Number(textureCoordinates !== -1);
    if (textureCoordinates !== -1 && !(geometry instanceof HsTexturedIndexedGeometry)) {
      return null;
    }
    if (collection !== null) {
      known += Number(color !== -1);
      for (let i: number = 0; i < INSTANCE_MATRIX_ATTRIBUTES.length; i++) {
        known += Number(shader.getAttribute(INSTANCE_MATRIX_ATTRIBUTES[i]) !== -1);
      }
    }
    if (positions === -1 || known !== shader.attributeCount) {
      return null;
    }
    const vertexArray: WebGLVertexArrayObject | null = gl.createVertexArray();
    if (vertexArray === null) {
      return null;
    }
    const bytes: number = HsGraphicConstants.FLOAT32_BYTES;
    const vertexStride: number = bytes * HsGraphicConstants.FLOATS_IN_VEC3;
    gl.bindVertexArray(vertexArray);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, geometry.indexBuffer);
    gl.bindBuffer(gl.ARRAY_BUFFER, geometry.buffer);
    gl.enableVertexAttribArray(positions);
    gl.vertexAttribPointer(positions, HsGraphicConstants.FLOATS_IN_VEC3, gl.FLOAT, false, vertexStride, 0);
    if (normals !== -1) {
      gl.enableVertexAttribArray(normals);
      gl.vertexAttribPointer(
        normals,
        HsGraphicConstants.FLOATS_IN_VEC3,
        gl.FLOAT,
        true,
        vertexStride,
        geometry.positionArrayLength * bytes
      );
    }
    if (textureCoordinates !== -1 && geometry instanceof HsTexturedIndexedGeometry) {
      gl.enableVertexAttribArray(textureCoordinates);
      gl.vertexAttribPointer(
        textureCoordinates,
        HsGraphicConstants.FLOATS_IN_VEC2,
        gl.FLOAT,
        false,
        HsGraphicConstants.XY_POINT_BYTES,
        (geometry.positionArrayLength + geometry.normalArrayLength) * bytes
      );
    }
    if (collection !== null) {
      const bytesPerVec: number = bytes * 4;
      gl.bindBuffer(gl.ARRAY_BUFFER, collection.matrixBuffer);
      for (let i: number = 0; i < INSTANCE_MATRIX_ATTRIBUTES.length; i++) {
        const location: number = shader.getAttribute(INSTANCE_MATRIX_ATTRIBUTES[i]);
        if (location === -1) {
          continue;
        }
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, 4, gl.FLOAT, false, bytesPerVec * 4, i * bytesPerVec);
        gl.vertexAttribDivisor(location, 1);
      }
      if (color !== -1) {
        gl.bindBuffer(gl.ARRAY_BUFFER, collection.colorBuffer);
        gl.enableVertexAttribArray(color);
        gl.vertexAttribPointer(color, 4, gl.FLOAT, false, bytesPerVec, 0);
        gl.vertexAttribDivisor(color, 1);
      }
    }
    return {
      vertexArray,
      layout,
      geometry,
      collection,
      matrixBuffer: collection?.matrixBuffer ?? null,
      colorBuffer: collection?.colorBuffer ?? null,
      usedAt: now,
    };
  }
}
